import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, first, forkJoin, interval, Observable, of, switchMap, tap} from 'rxjs';
import {AuthService} from 'src/app/auth/services/auth/auth.service';
import {DeviceAuthService} from 'src/app/auth/services/device-auth/device-auth.service';
import {WaiterAuthService} from 'src/app/auth/services/waiter-auth/waiter-auth.service';
import {AccountStorePort} from 'src/app/core/entities/account/domain/ports/account-store.port';
import {ACCOUNT_STORE} from 'src/app/core/entities/account/domain/tokens/account-store.token';
import {
  SESSION_STORAGE_REPOSITORY
} from 'src/app/core/entities/session/infrastructure/interfaces/session-storage.repository';
import {SocketRoomHandlerService} from 'src/app/core/services/socket/socket-room-handler.service';
import {AccountStepsService} from '../../entities/account/account-steps/account-steps.service';
import {AuditService} from '../../entities/audit/audit.service';
import {ConsumptionSocketHandlerService} from '../../entities/consumption/consumption-socket-handler.service';
import {ConsumptionService} from '../../entities/consumption/consumption.service';
import {DeviceSocketHandlerService} from '../../entities/device/device-socket-handler.service';
import {DeviceStatusService} from '../../entities/device/device-status.service';
import {DeviceService} from '../../entities/device/device.service';
import {DrawerSocketHandlerService} from '../../entities/drawer/drawer-socket-handler.service';
import {EventBufferService} from '../../entities/event-buffer/event-buffer.service';
import {IEventBuffer} from '../../entities/event-buffer/interface/event-buffer.interface';
import {LocationSocketHandlerService} from '../../entities/location/location-socket-handler.service';
import {
  OrganizationRepositoryAdapter
} from '../../entities/organization/infrastructure/adapters/organization-repository.adapter';
import {ProductFolderSocketHandlerService} from '../../entities/product-folder/product-folder-socket-handler.service';
import {ProductFolderService} from '../../entities/product-folder/product-folder.service';
import {ProductFormatService} from '../../entities/product-format/product-format.service';
import {ProductSocketHandlerService} from '../../entities/product/product-socket-handler.service';
import {ProductService} from '../../entities/product/product.service';
import {SaleFormatService} from '../../entities/sale-format/sale-format.service';
import {ServicesSocketHandlerService} from '../../entities/service/services-socket-handler.service';
import {ServicesService} from '../../entities/service/services.service';
import {SignatureService} from '../../entities/service/signature.service';
import {TicketEventService} from '../../entities/ticket-events/ticket-event.service';
import {TICKET_REPOSITORY} from '../../entities/ticket/domain/tokens/ticket.token';
import {WaiterSocketHandlerService} from '../../entities/waiter/waiter-socket-handler.service';
import {SOCKET_ACCOUNT_EVENT} from '../../enums/events-buffer/socket/socket-account-event.enum';
import {Logger} from '../../interfaces/Logger';
import {BluetoothManagerService} from '../bluetooth-manager/bluetooth-manager.service';
import {NavigationService} from '../navigation/navigation.service';
import {SocketService} from '../socket/socket.service';
import {TerminalManagerService} from '../terminal-manager/terminal-manager.service';
import {TrackingService} from '../tracking/tracking.service';
import {BOOKING_SCROLL_POSITION_REPOSITORY} from "../../entities/booking/domain/tokens/booking.tokens";
import {AppDisconnectedEvent} from '@platform/event-bus/domain/events/app-disconnected.event';
import {WalletWidgetFacade} from "@features/wallet/application/facades/wallet-widget.facade";
import {EVENT_BUS} from "@shared/domain/tokens/event-bus.token";

/**
 * Service responsible for cleaning application data in different scenarios.
 *
 * This service handles:
 * - Complete data clearing when signing out
 * - Partial data clearing for specific scenarios (waiter sign out, service sign out, etc.)
 * - Cleaning up socket subscriptions and connections
 * - Coordinating the cleanup of data across multiple services
 *
 * It centralizes all cleanup operations to ensure data consistency across the application.
 */
@Injectable({
  providedIn: 'root',
})
export class CleanerService {
  private readonly productService = inject(ProductService);
  private readonly productFolderService = inject(ProductFolderService);
  private readonly organizationService = inject(OrganizationRepositoryAdapter);
  private readonly servicesService = inject(ServicesService);
  private readonly deviceAuthService = inject(DeviceAuthService);
  private readonly waiterAuthService = inject(WaiterAuthService);
  private readonly authService = inject(AuthService);
  private readonly deviceService = inject(DeviceService);
  private readonly accountStepsService = inject(AccountStepsService);
  private readonly socketRoomHandlerService = inject(SocketRoomHandlerService);
  private readonly navigationService = inject(NavigationService);
  private readonly socketService = inject(SocketService);
  private readonly deviceSocketHandlerService = inject(DeviceSocketHandlerService);
  private readonly productSocketHandlerService = inject(ProductSocketHandlerService);
  private readonly productFolderSocketHandlerService = inject(ProductFolderSocketHandlerService);
  private readonly servicesSocketHandlerService = inject(ServicesSocketHandlerService);
  private readonly waiterSocketHandlerService = inject(WaiterSocketHandlerService);
  private readonly locationSocketHandlerService = inject(
    LocationSocketHandlerService
  );
  private readonly drawerSocketHandlerService = inject(
    DrawerSocketHandlerService
  );
  private readonly consumptionSocketHandlerService = inject(
    ConsumptionSocketHandlerService
  );

  private readonly deviceStatusService = inject(DeviceStatusService);
  private readonly signatureService = inject(SignatureService);
  private readonly consumptionService = inject(ConsumptionService);
  private readonly auditService = inject(AuditService);
  private readonly productFormatsService = inject(ProductFormatService);
  private readonly saleFormatsService = inject(SaleFormatService);
  private readonly terminalService = inject(TerminalManagerService);
  private readonly bluetoothService = inject(BluetoothManagerService);
  private readonly ticketEventService = inject(TicketEventService);
  private readonly trackingService = inject(TrackingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly eventBufferService = inject(EventBufferService);
  private readonly logger = inject(Logger);
  private readonly sessionStorageRepository = inject(
    SESSION_STORAGE_REPOSITORY
  );
  private readonly accountStoreService = inject<AccountStorePort>(ACCOUNT_STORE);
  private readonly ticketRepository = inject(TICKET_REPOSITORY);
  private readonly scrollPositionRepository = inject(BOOKING_SCROLL_POSITION_REPOSITORY);
  private readonly walletWidgetFacade = inject(WalletWidgetFacade);
  private readonly eventBus = inject(EVENT_BUS);

  /**
   * Initialize the service and set up event listeners for various cleanup scenarios.
   *
   * The constructor sets up multiple event listeners to react to different
   * application states that require data cleanup, including:
   * - Socket connections
   * - Authentication events (sign out)
   * - Device status changes
   * - Service status changes
   */
  constructor() {
    this.onSocketConnectedListener();
    this.onWaiterSignOutListener();
    this.onServiceSignOutListener();
    this.onDeviceRemovedListener();
    this.onDeviceIdleNavigationListener();
    this.onServiceIdleNavigationListener();
    this.onDeviceSignOutListener();
    this.onCleanDataFromOldServiceListener();
    this.eventGarbageCollectorInterval();
  }

  private eventGarbageCollectorInterval(): void {
    interval(1000 * 60 * 5)
      .pipe(
        switchMap(() => this.eventBufferService.getEventsBufferFromIndexedDB()),
        switchMap((events: IEventBuffer[]) => {
          this.logger.debug(
            `[Socket EVENTS 🔌🧩] [Cleaning up old events] [${events.length}]`
          );

          const syncAccountsToRemove = events.filter(
            (event) => event.name === SOCKET_ACCOUNT_EVENT.syncAccounts
          );

          const notRecordedEventsToRemove = events.filter(
            (event) => event.isRecorded === false
          );

          const eventsToRemove = [
            ...syncAccountsToRemove,
            ...notRecordedEventsToRemove,
          ];

          if (eventsToRemove.length > 0) {
            this.logger.debug(
              `[Socket EVENTS 🔌🧩] [Removing events] [${eventsToRemove.length}] [syncAccounts: ${syncAccountsToRemove.length}] [notRecorded: ${notRecordedEventsToRemove.length}]`
            );

            return forkJoin(
              eventsToRemove.map((event) =>
                this.eventBufferService.deleteEventBuffer(event._id)
              )
            );
          }

          return of(void 0);
        }),
        catchError((error: Error) => {
          this.logger.error(
            new Error(`[Socket EVENTS 🔌🧩] [Error cleaning up old events]`),
            error
          );

          return of(void 0);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Clears all data saved in IndexedDB including wallet data.
   *
   * This is a complete reset of the application data, used primarily
   * when a user signs out completely from the device. It performs cleanup on:
   * - Product data
   * - Waiters and accounts
   * - Organization data
   * - Services and drawers
   * - Location information
   * - Authentication tokens
   * - Socket subscriptions
   * - Terminal settings
   * - Navigation history
   * - Tickets and consumptions
   *
   * @returns {Observable<unknown>} An Observable that completes when all data deletions are finished
   */
  clearAllData(): Observable<unknown> {
    const dependencies$ = forkJoin({
      ticketEvent: this.ticketEventService.deleteCurrentTicketEvent(),
      auth: this.authService.deleteAuthToken(),
      consumptions: this.consumptionService.clearAllConsumptions(),
      session: this.sessionStorageRepository.clear(),
    });

    this.walletWidgetFacade.remove();
    this.productService.removeAll();
    this.accountStoreService.clearAccounts()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe();
    this.productFolderService.removeAll();
    this.organizationService.removeAll();
    this.servicesService.removeService();
    this.accountStepsService.clearData();
    this.socketRoomHandlerService.clearData();
    this.navigationService.clearData();
    this.signatureService.deleteSignatureFromDB();
    this.ticketRepository.clearAllTickets();
    this.ticketRepository.removeLastTicket();
    this.auditService.removeAll();
    this.productFormatsService
      .removeAll()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe();
    this.saleFormatsService.removeAll();

    this.terminalService.removeCurrentTerminal();
    this.terminalService.removeTap2PayStatus();
    this.terminalService.removeCodeReaderNYCStatus();
    this.bluetoothService.removeCurrentPrinter();
    this.deviceService.deleteDevice();
    this.trackingService.clearAll();

    this.productFormatsService.removeAll()
    this.saleFormatsService.removeAll()

    this.eventBus.publish(new AppDisconnectedEvent());

    return dependencies$.pipe(first(), takeUntilDestroyed(this.destroyRef));
  }

  clearWaiterData(isPersonalDevice: boolean): Observable<void> {
    return this.deleteCurrentTicketEvent().pipe(
      first(),
      tap(() => this.clearAccountSteps()),
      tap(() => !isPersonalDevice && this.clearSelectedAccount()),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  private deleteCurrentTicketEvent(): Observable<void> {
    return this.ticketEventService.deleteCurrentTicketEvent();
  }

  private clearAccountSteps(): void {
    this.accountStepsService.clearData();
  }

  private clearSelectedAccount(): void {
    this.accountStoreService.saveSelectedAccount(undefined);
  }

  /**
   * Clears all service-related data.
   *
   * This is used when transitioning between services or when
   * a service is completed/signed out. It removes:
   * - Products and folders
   * - Waiters and accounts
   * - Service information
   * - Drawers and locations
   * - Navigation data
   * - Tickets and consumptions
   * - Wallet data
   *
   * @returns {Observable<unknown>} An Observable that completes when service data cleanup is finished
   */
  clearServiceData(): Observable<unknown> {
    const dependencies$ = forkJoin({
      session: this.sessionStorageRepository.clear(),
      consumptions: this.consumptionService.clearAllConsumptions(),
      scrollPositions: this.scrollPositionRepository.cleanup(),
    });

    this.walletWidgetFacade.remove();
    this.productService.removeAll();
    this.accountStoreService.clearAccounts()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe();
    this.productFolderService.removeAll();
    this.servicesService.removeService();
    this.accountStepsService.clearData();
    this.navigationService.clearData();
    this.ticketRepository.clearAllTickets();
    this.ticketRepository.removeLastTicket();
    this.auditService.removeAll();
    this.trackingService.clearAll();

    this.productFormatsService.removeAll()
    this.saleFormatsService.removeAll()

    return dependencies$.pipe(first(), takeUntilDestroyed(this.destroyRef));
  }

  /**
   * Sets up a listener for waiter sign out events to trigger data cleanup.
   *
   * When a waiter signs out, this handler cleans up appropriate data based on device type.
   *
   * @private
   */
  private onWaiterSignOutListener(): void {
    this.waiterAuthService.onWaiterSignOut$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.deviceService.getDevice()),
        switchMap((device) => this.clearWaiterData(!!device?.personal_use))
      )
      .subscribe();
  }

  /**
   * Sets up a listener for service completion events to trigger data cleanup.
   *
   * When a service is finished, this handler cleans up all service-related data.
   *
   * @private
   */
  private onServiceSignOutListener(): void {
    this.servicesService.onServiceFinished$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.clearServiceData())
      )
      .subscribe();
  }

  /**
   * Sets up a listener for device removal events to trigger data cleanup.
   *
   * When a device is removed, this handler performs a service data cleanup.
   *
   * @private
   */
  private onDeviceRemovedListener(): void {
    this.deviceService.onDeviceRemoved$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.clearServiceData())
      )
      .subscribe();
  }

  /**
   * Sets up a listener to clean data from old services when a trigger event occurs.
   *
   * When the old service cleanup event is triggered, this handler removes specific data
   * related to the previous service.
   *
   * @private
   */
  private onCleanDataFromOldServiceListener(): void {
    this.deviceStatusService.onCleanDataFromOldService$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() =>
          forkJoin([this.consumptionService.clearAllConsumptions()])
        ),
        switchMap(() => this.accountStoreService.clearAccounts()),
        tap(() => {
          this.ticketRepository.clearAllTickets();
          this.ticketRepository.removeLastTicket();
        })
      )
      .subscribe();
  }

  /**
   * Sets up a listener for device sign out events to trigger complete data cleanup.
   *
   * When a device signs out, this handler performs a full data reset including localStorage.
   *
   * @private
   */
  private onDeviceSignOutListener(): void {
    this.deviceAuthService.onDeviceSignOut$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          return this.clearAllData();
        })
      )
      .subscribe(() => {
        localStorage.clear();
      });
  }

  /**
   * Sets up a listener for device idle navigation events to trigger service data cleanup.
   *
   * When a device enters idle state, this handler cleans up service data.
   *
   * @private
   */
  private onDeviceIdleNavigationListener(): void {
    this.deviceStatusService.onDeviceIdleNavigation$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.clearServiceData())
      )
      .subscribe();
  }

  /**
   * Sets up a listener for service idle navigation events to trigger service data cleanup.
   *
   * When a service enters idle state, this handler cleans up service data.
   *
   * @private
   */
  private onServiceIdleNavigationListener(): void {
    this.deviceStatusService.onServiceIdleNavigation$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.clearServiceData())
      )
      .subscribe();
  }

  /**
   * Sets up a listener for socket connection events to clear socket subscriptions.
   *
   * When socket connection is established, this handler clears existing subscriptions
   * to prevent duplicates when reconnecting to rooms.
   *
   * @private
   */
  private onSocketConnectedListener(): void {
    this.socketService.onSocketConnected$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.deviceSocketHandlerService.clearSubscriptions();
        this.productSocketHandlerService.clearSubscriptions();
        this.productFolderSocketHandlerService.clearSubscriptions();
        this.servicesSocketHandlerService.clearSubscriptions();
        this.waiterSocketHandlerService.clearSubscriptions();
        this.locationSocketHandlerService.clearSubscriptions();
        this.drawerSocketHandlerService.clearSubscriptions();
        this.consumptionSocketHandlerService.clearSubscriptions();
      });
  }
}
